import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Plus, Star, Mail, Phone, Building2, 
  MoreHorizontal, Edit, Trash2, User, X, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import api from '../services/api';

export const ContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    title: ''
  });

  // Load contacts from API
  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setIsLoading(true);
      const response = await api.getContacts(null, false, 1, 100);
      setContacts(response.contacts || []);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery) {
        try {
          const response = await api.getContacts(searchQuery, false, 1, 100);
          setContacts(response.contacts || []);
        } catch (error) {
          console.error('Search failed:', error);
        }
      } else {
        loadContacts();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Group contacts by first letter
  const groupedContacts = contacts.reduce((groups, contact) => {
    const letter = contact.name.charAt(0).toUpperCase();
    if (!groups[letter]) groups[letter] = [];
    groups[letter].push(contact);
    return groups;
  }, {});

  const sortedLetters = Object.keys(groupedContacts).sort();

  const handleAddContact = async () => {
    if (!newContact.name || !newContact.email) return;
    
    try {
      setIsSaving(true);
      await api.createContact(newContact);
      setIsAddDialogOpen(false);
      setNewContact({ name: '', email: '', phone: '', company: '', title: '' });
      loadContacts();
    } catch (error) {
      console.error('Failed to add contact:', error);
      alert(error.message || 'Kişi eklenemedi');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteContact = async (id) => {
    if (!confirm('Bu kişiyi silmek istediğinizden emin misiniz?')) return;
    
    try {
      await api.deleteContact(id);
      setSelectedContact(null);
      loadContacts();
    } catch (error) {
      console.error('Failed to delete contact:', error);
      alert(error.message || 'Kişi silinemedi');
    }
  };

  const handleToggleFavorite = async (id) => {
    try {
      const result = await api.toggleContactFavorite(id);
      setContacts(prev => prev.map(c => 
        c.id === id ? { ...c, is_favorite: result.is_favorite } : c
      ));
      if (selectedContact?.id === id) {
        setSelectedContact(prev => ({ ...prev, is_favorite: result.is_favorite }));
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  return (
    <div className="flex-1 flex h-full" data-testid="contacts-page">
      {/* Contact List */}
      <div className={`flex flex-col bg-background border-r border-border/50 ${selectedContact ? 'hidden md:flex w-[350px]' : 'w-full md:w-[350px]'}`}>
        {/* Header */}
        <div className="p-4 border-b border-border/50 space-y-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold">Kişiler</h1>
            <Button 
              onClick={() => setIsAddDialogOpen(true)}
              size="sm" 
              className="rounded-full"
              data-testid="add-contact-button"
            >
              <Plus className="h-4 w-4 mr-1" />
              Ekle
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Kişi ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10 rounded-xl bg-secondary/50 border-0"
              data-testid="contact-search-input"
            />
          </div>
        </div>

        {/* Contact List */}
        <ScrollArea className="flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : sortedLetters.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <User className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-sm">Kişi bulunamadı</p>
            </div>
          ) : (
            <div className="divide-y divide-border/30">
              {sortedLetters.map((letter) => (
                <div key={letter}>
                  <div className="px-4 py-2 bg-muted/30 sticky top-0">
                    <span className="text-xs font-semibold text-muted-foreground uppercase">
                      {letter}
                    </span>
                  </div>
                  {groupedContacts[letter].map((contact, index) => (
                    <motion.button
                      key={contact.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      onClick={() => setSelectedContact(contact)}
                      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left ${selectedContact?.id === contact.id ? 'bg-accent' : ''}`}
                      data-testid={`contact-item-${contact.id}`}
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={contact.avatar_url} />
                        <AvatarFallback className="bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 text-blue-700 dark:text-blue-200 font-semibold">
                          {contact.name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{contact.name}</span>
                          {contact.is_favorite && (
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400 shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{contact.email}</p>
                      </div>
                    </motion.button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="p-3 border-t border-border/50 text-center">
          <p className="text-xs text-muted-foreground">{contacts.length} kişi</p>
        </div>
      </div>

      {/* Contact Detail */}
      <div className={`flex-1 bg-background ${!selectedContact ? 'hidden md:flex' : 'flex'}`}>
        {selectedContact ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex-1 flex flex-col"
          >
            {/* Detail Header */}
            <div className="flex items-center justify-between p-4 border-b border-border/50">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedContact(null)}
                className="md:hidden rounded-lg"
                data-testid="contact-back-button"
              >
                <X className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-lg" 
                  data-testid="favorite-contact"
                  onClick={() => handleToggleFavorite(selectedContact.id)}
                >
                  <Star className={`h-4 w-4 ${selectedContact.is_favorite ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                </Button>
                <Button variant="ghost" size="icon" className="rounded-lg" data-testid="edit-contact">
                  <Edit className="h-4 w-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-lg">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem 
                      className="text-destructive"
                      onClick={() => handleDeleteContact(selectedContact.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Sil
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Detail Content */}
            <ScrollArea className="flex-1">
              <div className="p-6 max-w-xl mx-auto">
                {/* Avatar & Name */}
                <div className="text-center mb-8">
                  <Avatar className="h-24 w-24 mx-auto mb-4">
                    <AvatarImage src={selectedContact.avatar_url} />
                    <AvatarFallback className="bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 text-blue-700 dark:text-blue-200 text-2xl font-semibold">
                      {selectedContact.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <h2 className="text-2xl font-semibold">{selectedContact.name}</h2>
                  {selectedContact.title && selectedContact.company && (
                    <p className="text-muted-foreground">{selectedContact.title} · {selectedContact.company}</p>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="flex justify-center gap-4 mb-8">
                  <Button variant="outline" className="rounded-full" data-testid="email-contact">
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </Button>
                  <Button variant="outline" className="rounded-full" data-testid="call-contact">
                    <Phone className="h-4 w-4 mr-2" />
                    Ara
                  </Button>
                </div>

                {/* Contact Info */}
                <div className="space-y-4">
                  <div className="p-4 bg-muted/30 rounded-xl">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Email</p>
                    <p className="font-medium">{selectedContact.email}</p>
                  </div>
                  {selectedContact.phone && (
                    <div className="p-4 bg-muted/30 rounded-xl">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Telefon</p>
                      <p className="font-medium">{selectedContact.phone}</p>
                    </div>
                  )}
                  {selectedContact.company && (
                    <div className="p-4 bg-muted/30 rounded-xl">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Şirket</p>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <p className="font-medium">{selectedContact.company}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          </motion.div>
        ) : (
          <div className="flex-1 flex items-center justify-center" data-testid="no-contact-selected">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <User className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-1">Kişi Seçilmedi</h3>
              <p className="text-sm text-muted-foreground">Detayları görmek için bir kişi seçin</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Contact Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-contact-dialog">
          <DialogHeader>
            <DialogTitle>Yeni Kişi Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Ad Soyad *</Label>
              <Input
                id="name"
                value={newContact.name}
                onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ahmet Yılmaz"
                data-testid="new-contact-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                placeholder="ahmet@example.com"
                data-testid="new-contact-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Telefon</Label>
              <Input
                id="phone"
                value={newContact.phone}
                onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+90 532 123 4567"
                data-testid="new-contact-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Şirket</Label>
              <Input
                id="company"
                value={newContact.company}
                onChange={(e) => setNewContact(prev => ({ ...prev, company: e.target.value }))}
                placeholder="ABC Şirketi"
                data-testid="new-contact-company"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Ünvan</Label>
              <Input
                id="title"
                value={newContact.title}
                onChange={(e) => setNewContact(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Satış Müdürü"
                data-testid="new-contact-title"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleAddContact} 
              disabled={isSaving || !newContact.name || !newContact.email}
              data-testid="save-contact-button"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsPage;
